export type LifecycleKind = "unpublished" | "live" | "live-edits" | "paused" | "paused-edits" | "archived";

export interface LifecycleState {
  kind: LifecycleKind;
  /** Currently published version number, or null when never published */
  publishedVersion: number | null;
  /** Whether automatic triggers (schedules, webhooks, on-chain events) fire */
  enabled: boolean;
  /** True when current snapshot differs from published snapshot */
  hasEdits: boolean;
  /** Exact count when supplied by editor; null otherwise */
  editCount: number | null;
}

/** Minimum workflow shape this derivation needs. Matches current API. */
export interface WorkflowLifecycleInput {
  id: string;
  name: string;
  status: "draft" | "active" | "paused" | "archived";
  version: number;
  hasDraft?: boolean;
  hasLiveVersion?: boolean;
  archivedAt?: string | null;
  updatedAt?: string;
}

export interface EditorOverrides {
  /** Explicit count of unsaved edits (wins over hasDraft boolean). */
  editCount?: number;
  /**
   * When the editor is viewing the draft row of a published workflow
   * (status="draft" + hasLiveVersion=true), the draft row itself only
   * carries the draft's version number. Pass the published version number
   * here so the chip/badge shows "Live · vN" instead of losing the context.
   */
  publishedVersion?: number;
  /**
   * When viewing the draft row of a published workflow, set true if the
   * live counterpart is paused. Drives Paused + Edits vs Live + Edits.
   */
  publishedPaused?: boolean;
}

/** True for states that have a published version (live, live-edits, paused, paused-edits). */
export function isPublishedState(state: LifecycleState): boolean {
  return state.kind !== "unpublished" && state.kind !== "archived";
}

export function resolveLifecycleState(workflow: WorkflowLifecycleInput, editor: EditorOverrides = {}): LifecycleState {
  // Archived short-circuits every other flag.
  if (workflow.status === "archived" || workflow.archivedAt) {
    return {
      kind: "archived",
      publishedVersion: null,
      enabled: false,
      hasEdits: false,
      editCount: null,
    };
  }

  const hasEditsFromEditor = editor.editCount != null && editor.editCount > 0;
  const editCount = editor.editCount ?? null;

  if (workflow.status === "draft") {
    // Draft row of an already-published workflow: backend returns
    // status="draft" + hasLiveVersion=true for this case. Treat as edits on
    // top of live/paused so the chip stays consistent with the parent row.
    if (workflow.hasLiveVersion) {
      const livePaused = editor.publishedPaused === true;
      return {
        kind: livePaused ? "paused-edits" : "live-edits",
        publishedVersion: editor.publishedVersion ?? null,
        enabled: !livePaused,
        hasEdits: true,
        editCount,
      };
    }
    // Genuinely new workflow — no published counterpart.
    return {
      kind: "unpublished",
      publishedVersion: null,
      enabled: false,
      hasEdits: false,
      editCount: null,
    };
  }

  const hasEdits = hasEditsFromEditor || Boolean(workflow.hasDraft);
  const published = workflow.version;

  if (workflow.status === "active") {
    return {
      kind: hasEdits ? "live-edits" : "live",
      publishedVersion: published,
      enabled: true,
      hasEdits,
      editCount,
    };
  }

  // Paused
  return {
    kind: hasEdits ? "paused-edits" : "paused",
    publishedVersion: published,
    enabled: false,
    hasEdits,
    editCount,
  };
}
